//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  IContentLibraryItem,
  IContentLibrarySourcesOptions,
  MediaFilterParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import { Alert } from '@components/atoms/alert/Alert';
import { ErrorMessage } from '@components/media-filters/ErrorMessage';
import {
  FilterSection,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { useContentLibraryItemsInfiniteQuery } from '@components/media-filters/useContentLibraryItemsInfiniteQuery';
import { useContentLibraryParamsMapper } from '@components/media-filters/useContentLibraryParamsMapper';
import { EmptyMediaPage } from '@content-drawer/media/EmptyMediaPage';
import { EmptyMediaPanel } from '@content-drawer/media/EmptyMediaPanel';
import { InfiniteScroll } from '@content-drawer/media/InfiniteScroll';
import { MediaItem } from '@content-drawer/media/MediaItem';
import { PreviewPopup } from '@content-drawer/media/PreviewPopup';
import { isVideoProcessing } from '@content-drawer/media/mediaUtils';
import { getContextualHelp } from '@content-drawer/selectors/getContentDrawerSettings';
import {
  Upload,
  UploadContext,
  UploadEvent,
} from '@content-drawer/upload/UploadContextProvider';
import { UploadStatus } from '@content-drawer/upload/UploadStatus';
import {
  ProcessingConfig,
  checkIfAnyVideoProcessed,
} from '@content-drawer/upload/checkIfAnyVideoProcessed';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { UniversalItemType } from '@models/UniversalItemType';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { isDefined } from '@utils/filterPredicates';

export const CONTENT_DRAWER_MEDIA_LIMIT = 45;

export interface MediaPanelContentProps {
  onResetFilters: () => void;
  onPreviewItem: (item: IContentLibraryItem | undefined) => void;
  onSelectItem: (item: IContentLibraryItem) => void;
  previewItem: IContentLibraryItem | undefined;
  sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>;
  selectedItems: Map<string, IContentLibraryItem>;
  sources: IContentLibrarySourcesOptions;
  toggleFilters: (setter: (state: boolean) => boolean) => void;
}

export const MediaPanelContent = ({
  onResetFilters,
  onPreviewItem,
  onSelectItem,
  previewItem,
  sections,
  selectedItems,
  sources,
  toggleFilters,
}: MediaPanelContentProps) => {
  const [isContextualHelpClosed, setIsContextualHelpClosed] = useState(false);
  const { filters, filtersCount } = useContentLibraryParamsMapper({ sections });
  const contextualHelp = useSelector(getContextualHelp);
  const settings = useSelector((state) => state.contentDrawer.drawerSettings);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { getCompletedUploads, onUploadEvent } = useContext(UploadContext);

  const { data, error, hasNextPage, fetchNextPage, isFetching, isLoading } =
    useContentLibraryItemsInfiniteQuery({
      query: {
        filters,
        limit: CONTENT_DRAWER_MEDIA_LIMIT,
        sources,
      },
    });

  const onLoadMore = async () => {
    if (hasNextPage && !error && !isFetching) {
      await fetchNextPage();
    }
  };

  const togglePreviewPopup = (item?: IContentLibraryItem) => {
    if (previewItem === item || !isDefined(item)) {
      toggleFilters(() => true); // Show filters on the close preview popup event
      onPreviewItem(undefined);
    } else {
      toggleFilters(() => false); // Hide filters on the preview media event
      onPreviewItem(item);
    }
  };

  /**
   * We need to select an item after it has been uploaded.
   * However, sometimes we need to wait for the backend to process the videos.
   * Once processing is complete, we will select the item and remove the upload.
   */
  const selectLastUploadedItem = useCallback(
    (contentItems: IContentLibraryItem[], completedUploads: Upload[]) => {
      const ids = completedUploads.map((item) => item.resultId);
      const item = contentItems.find(
        (item) => ids.includes(item.id) && !isVideoProcessing(item),
      );

      if (isDefined(item)) {
        const lastCompletedUpload = completedUploads.find(
          (upload) => upload.resultId === item.id,
        );

        if (isDefined(lastCompletedUpload)) {
          onUploadEvent(UploadEvent.CLEAN, lastCompletedUpload);
          onSelectItem(item);
        }
      }
    },
    [],
  );

  /**
   * Whenever we receive new media items, we need to identify the items that are still processing.
   * If there are processing items, we must recursively check their status.
   * Once we confirm that processing has finished, we need to reload the data.
   * If the user triggers filtering, we need to cancel the previous check and initiate a new one.
   */
  const startVideoProcessingCheck = useCallback(
    (contentItems: IContentLibraryItem[], config: ProcessingConfig) => {
      const processingIds = contentItems
        .filter(isDefined)
        .filter(isVideoProcessing)
        .map((item) => item.id);

      if (isDefined(processingIds) && processingIds.length > 0) {
        checkIfAnyVideoProcessed(processingIds, config)
          .catch(() => {
            dispatch(
              toastNotificationsActions.addToastNotification({
                title: 'Video Processing Check Issue',
                message:
                  'We are facing some issues with the processing check. Reload the page, please.',
                type: ToastType.ERROR,
                options: {
                  toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
                },
              }),
            );
          })
          .finally(() => {
            if (!config.isCancelled) {
              queryClient.invalidateQueries([QueryKeyConstants.MediaFilters]);
            }
          });
      }
    },
    [],
  );

  useEffect(() => {
    const config = { isCancelled: false };
    const contentItems = data?.pages.flatMap((item) => item.contentItems) ?? [];
    const completedUploads = getCompletedUploads();
    selectLastUploadedItem(contentItems, completedUploads);
    startVideoProcessingCheck(contentItems, config);

    return () => {
      config.isCancelled = true;
    };
  }, [data]);

  const shouldShowEmptyPage =
    !isLoading &&
    (data?.pages.length === 0 ||
      (data?.pages.length === 1 && data.pages[0]?.contentItems.length === 0));
  const hasMedia = shouldShowEmptyPage && filtersCount === 0;

  return (
    <InfiniteScroll
      isEnabled={hasNextPage ?? true}
      isLoading={isFetching}
      onLoadMore={onLoadMore}
    >
      <PreviewPopup item={previewItem} onClose={togglePreviewPopup} />
      {contextualHelp && !isContextualHelpClosed && (
        <HelpAlert
          onDismiss={() => setIsContextualHelpClosed(true)}
          type='info'
        >
          {contextualHelp.message}
        </HelpAlert>
      )}
      <UploadStatus />
      {hasMedia && <EmptyMediaPanel />}
      {!hasMedia && shouldShowEmptyPage && (
        <EmptyMediaPage onResetFilters={onResetFilters} />
      )}
      {error && <ErrorMessageText error={error.message} />}
      <MediaItems>
        {data?.pages
          .flatMap((item) => item.contentItems)
          .map((item) =>
            settings.scope === 'Scheduler' &&
            item.itemType === UniversalItemType.TikTok ? null : (
              <MediaItem
                key={item.id}
                item={item}
                isSelected={isDefined(selectedItems.get(item.id))}
                onPreview={togglePreviewPopup}
                onSelect={onSelectItem}
              />
            ),
          )}
      </MediaItems>
    </InfiniteScroll>
  );
};

const MediaItems = styled.ul(
  ({ theme }) => `
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: ${theme.spacings[2]};
    padding: ${theme.spacings[3]};
    list-style: none;
  `,
);

const ErrorMessageText = styled(ErrorMessage)(
  ({ theme }) => `
    padding: ${theme.spacings[3]};
  `,
);

const HelpAlert = styled(Alert)(
  ({ theme }) => `
    margin: ${theme.spacings[0]} ${theme.spacings[3]};
  `,
);
